// Browser-safe logger. Same `Logger` shape as `@/lib/log` so call-site code is
// identical, but no AsyncLocalStorage (browser doesn't have node:async_hooks)
// and no per-request id correlation. Error/warn still route to Sentry via the
// browser SDK that @sentry/nextjs ships.
//
// Use this from client components and other browser-only code. Server code
// should keep importing `@/lib/log` for full features.

import { sentryCall } from './sentry-client'

export interface Logger {
  error: (msg: unknown, ...args: unknown[]) => void
  warn: (msg: unknown, ...args: unknown[]) => void
  info: (msg: unknown, ...args: unknown[]) => void
  debug: (msg: unknown, ...args: unknown[]) => void
  trace: (msg: unknown, ...args: unknown[]) => void
  sql: (msg: unknown, ...args: unknown[]) => void
}

const isDev =
  typeof window !== 'undefined' ? window.location.hostname === 'localhost' : process.env.NODE_ENV === 'development'

// Skip Sentry during `next build` prerendering: captureMessage/captureException
// synchronously read `new Date()` / `crypto.randomUUID()`, which Next 16 Cache
// Components forbids during a prerender — calling Sentry from a server component
// that runs at build time crashes the prerender. Mirrors the guard in
// ./index.ts. Runtime warnings/errors still report.
const isBuildPhase = process.env.NEXT_PHASE === 'phase-production-build'

const noop = () => {}

const PRINTF_SPEC = /%[sdjo]/

// printf-style substitution (%s, %d, %j, %o) so the Sentry body shows
// interpolated values instead of the raw template. Mirrors what
// console.log applies for browser DevTools output.
export const formatPrintf = (template: string, args: readonly unknown[]): string => {
  if (args.length === 0) return template
  let i = 0
  const body = template.replace(/%[sdjo]/g, (spec) => {
    if (i >= args.length) return spec
    const v = args[i++]
    if (spec === '%j' || spec === '%o') {
      try {
        return JSON.stringify(v)
      } catch {
        return String(v)
      }
    }
    return String(v)
  })
  // Append any args past the last specifier, the way console prints trailing
  // arguments — so `log.warn('got %s', a, extra)` doesn't drop `extra`.
  if (i >= args.length) {
    return body
  }
  const rest = args.slice(i).map((v) => {
    if (typeof v === 'string') {
      return v
    }
    try {
      return JSON.stringify(v)
    } catch {
      return String(v)
    }
  })
  return `${body} ${rest.join(' ')}`
}

const formatForSentry = (msg: unknown, args: readonly unknown[]): string => {
  if (typeof msg === 'string') return formatPrintf(msg, args)
  if (msg instanceof Error) return msg.message
  try {
    return JSON.stringify(msg)
  } catch {
    return String(msg)
  }
}

export const createLogger = (origin: string): Logger => {
  const make = (
    channel: 'error' | 'warn' | 'info' | 'debug' | 'trace',
    enabled: boolean
  ): ((msg: unknown, ...args: unknown[]) => void) => {
    if (!enabled) return noop
    const prefix = `[${channel}][${origin}]`
    const out =
      channel === 'error'
        ? console.error
        : channel === 'warn'
          ? console.warn
          : channel === 'info'
            ? console.info
            : console.log

    // Fold prefix into the format string when msg is a string so %s/%d
    // specifiers in msg resolve against args. See the same comment in
    // ./index.ts for why a separate first-arg prefix breaks formatting.
    const emit = (msg: unknown, ...args: unknown[]): void => {
      if (typeof msg === 'string') {
        // Interpolate printf specifiers here so Sentry's console integration
        // (which reads raw console args and doesn't apply printf in the browser)
        // records substituted values, not literal %s/%o. Messages without
        // specifiers keep their args so DevTools can expand errors/objects.
        if (PRINTF_SPEC.test(msg)) {
          out(`${prefix} ${formatPrintf(msg, args)}`)
        } else {
          out(`${prefix} ${msg}`, ...args)
        }
      } else {
        out(prefix, msg, ...args)
      }
    }

    const sentryFingerprint = (msg: unknown): string[] => (typeof msg === 'string' ? [origin, msg] : [origin])

    if (channel === 'error') {
      return (msg: unknown, ...args: unknown[]): void => {
        emit(msg, ...args)
        if (isDev || isBuildPhase) return
        const errArg = msg instanceof Error ? msg : (args.find((a) => a instanceof Error) as Error | undefined)
        const extra = args.length > 0 ? { args } : undefined
        sentryCall((Sentry) => {
          if (errArg) {
            Sentry.captureException(errArg, { tags: { origin }, extra })
          } else {
            Sentry.captureMessage(formatForSentry(msg, args), {
              level: 'error',
              tags: { origin },
              fingerprint: sentryFingerprint(msg),
              extra,
            })
          }
        })
      }
    }
    if (channel === 'warn') {
      return (msg: unknown, ...args: unknown[]): void => {
        emit(msg, ...args)
        if (isDev || isBuildPhase) return
        sentryCall((Sentry) =>
          Sentry.captureMessage(formatForSentry(msg, args), {
            level: 'warning',
            tags: { origin },
            fingerprint: sentryFingerprint(msg),
            extra: args.length > 0 ? { args } : undefined,
          })
        )
      }
    }
    return emit
  }

  return {
    error: make('error', true),
    warn: make('warn', true),
    info: make('info', isDev),
    debug: make('debug', isDev),
    trace: make('trace', false),
    sql: noop,
  }
}
